import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Card,
  IconArrow,
  IconTick,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';

import { Link, TextLink } from 'src/components/Link/Link';
import translations from 'src/pages/ConnectedPartners/.vocab';
import { TIERS } from 'src/tiers';
import type { Language, Partner } from 'src/types';
import { useConfig } from 'src/utils/hooks/useContext';
import { makePartnerImageUrl } from 'src/utils/partners/makePartnerImageUrl';
import { slugify } from 'src/utils/slugify';

import * as styles from './PartnerCard.css';

interface CardProps {
  partner: Partner;
}

const tierConfig = Object.fromEntries(
  TIERS.map(({ tier, ...config }) => [tier, config]),
);

export const PartnerCard = ({ partner }: CardProps) => {
  const { t } = useTranslations(translations);
  const {
    name,
    slug,
    siteUrl: website,
    partnerBlurbEN: blurbEN,
    partnerBlurbID: blurbID,
    partnerBlurbTH: blurbTH,
    imageUrl: icon,
    hasMiddlewareRequirement: middleware,
    hasApacIntegration: connectedApacIntegration,
    newPartner: isNew,
    adPerformancePanel: adPerformanceFeature,
    jobAdPreview: jobPreviewFeature,
    screeningQuestions: screeningFeature,
    tier: partnerTier,
  } = partner;

  const { language } = useConfig();
  const blurbMap: Partial<Record<Language, string | null | undefined>> = {
    'id-ID': blurbID,
    'th-TH': blurbTH,
  };
  const blurb = blurbMap[language] ?? blurbEN;

  const availableTypeCodes = partner.configurableRelationships.map(
    ({ typeCode }) => typeCode,
  );
  const hasJobPosting = availableTypeCodes.includes('JOB_POSTING');
  const hasOptimisedApply = availableTypeCodes.includes('APPLICATION_EXPORT');
  const hasApplyWithSeek = availableTypeCodes.includes('APPLICATION_PREFILL');

  const websiteWithProtocol = website?.startsWith('https://')
    ? website
    : `https://${website}`;

  const nameLabel = (
    <Text size="small" weight="strong">
      {website && tierConfig[partnerTier]?.showWebsite ? (
        <TextLink
          href={websiteWithProtocol}
          skipResolvedUrl={true}
          target="_blank"
          tracking={`${partnerTier}-${
            slug ?? slugify({ str: name })
          }-link-clicked`}
          weight="weak"
          icon={<IconArrow direction="right" />}
          iconPosition="trailing"
        >
          {name}
        </TextLink>
      ) : (
        name
      )}
    </Text>
  );
  return (
    <Card height="full">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="spaceBetween"
        height="full"
      >
        <Box paddingBottom="xxlarge">
          <Stack space="xlarge">
            <Stack space="large">
              {icon && tierConfig[partnerTier]?.showIcon ? (
                <Stack space="xsmall">
                  <Link
                    href={websiteWithProtocol}
                    skipResolvedUrl={true}
                    target="_blank"
                    tracking={`${partnerTier}-${
                      slug ?? slugify({ str: name })
                    }-link-clicked`}
                  >
                    <Box
                      component="img"
                      className={styles.partnerIcon}
                      src={makePartnerImageUrl(icon)}
                    />
                  </Link>
                </Stack>
              ) : null}

              <Stack space="small">
                {nameLabel}
                {blurb && tierConfig[partnerTier]?.showBlurb ? (
                  <Text size="small">{blurb}</Text>
                ) : null}
              </Stack>
            </Stack>
            <Stack space="xsmall">
              {partnerTier === 'CONNECTED_PARTNERS' ||
              partnerTier === 'CONNECTED_MIDDLEWARE' ? (
                <>
                  {(hasJobPosting || jobPreviewFeature) && (
                    <Inline space="xsmall" alignY="center">
                      {hasJobPosting && (
                        <>
                          <IconTick tone="positive" size="xsmall" />
                          <Text size="xsmall" tone="neutral" weight="strong">
                            {t('jobPosting')}
                          </Text>
                        </>
                      )}
                      {jobPreviewFeature && (
                        <Badge tone="neutral">{t('jobAdPreview')}</Badge>
                      )}
                    </Inline>
                  )}
                  {(hasOptimisedApply || screeningFeature) && (
                    <Inline space="xsmall" alignY="center">
                      {hasOptimisedApply && (
                        <>
                          <IconTick tone="positive" size="xsmall" />
                          <Text size="xsmall" tone="neutral" weight="strong">
                            {t('optimisedApply')}
                          </Text>
                        </>
                      )}
                      {screeningFeature && (
                        <Badge tone="neutral">{t('screeningQuestions')}</Badge>
                      )}
                    </Inline>
                  )}
                  {(hasApplyWithSeek || adPerformanceFeature) && (
                    <Inline space="xsmall" alignY="center">
                      {hasApplyWithSeek && (
                        <>
                          <IconTick tone="positive" size="xsmall" />
                          <Text size="xsmall" tone="neutral" weight="strong">
                            {t('applyWithSeek')}
                          </Text>
                        </>
                      )}
                      {adPerformanceFeature && (
                        <Badge tone="neutral">{t('AdPerformancePanel')}</Badge>
                      )}
                    </Inline>
                  )}
                </>
              ) : (
                <>
                  {hasJobPosting && (
                    <Inline space="xsmall" alignY="center">
                      <IconTick tone="positive" size="xsmall" />
                      <Text size="small" tone="neutral" weight="strong">
                        {t('jobPosting')}
                      </Text>
                    </Inline>
                  )}
                  {hasOptimisedApply && (
                    <Inline space="xsmall" alignY="center">
                      <IconTick tone="positive" size="xsmall" />
                      <Text size="small" tone="neutral" weight="strong">
                        {t('optimisedApply')}
                      </Text>
                    </Inline>
                  )}
                  {hasApplyWithSeek && (
                    <Inline space="xsmall" alignY="center">
                      <IconTick tone="positive" size="xsmall" />
                      <Text size="small" tone="neutral" weight="strong">
                        {t('applyWithSeek')}
                      </Text>
                    </Inline>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Box>
        <Box paddingY="xxsmall">
          <Stack space="large">
            {partnerTier !== 'CONNECTED_PARTNERS' &&
              partnerTier !== 'CONNECTED_MIDDLEWARE' &&
              (adPerformanceFeature ||
                jobPreviewFeature ||
                screeningFeature) && (
                <Stack space="xxsmall">
                  <Text size="xsmall" tone="secondary" weight="strong">
                    {t('features')}:
                  </Text>

                  <Inline space="xxsmall">
                    {adPerformanceFeature && (
                      <Badge tone="neutral">{t('AdPerformancePanel')}</Badge>
                    )}
                    {jobPreviewFeature && (
                      <Badge tone="neutral">{t('jobAdPreview')}</Badge>
                    )}
                    {screeningFeature && (
                      <Badge tone="neutral">{t('screeningQuestions')}</Badge>
                    )}
                  </Inline>
                </Stack>
              )}

            <Inline space="xxsmall">
              {middleware && (
                <Badge tone="info" weight="regular">
                  {t('middlewareRequired')}
                </Badge>
              )}
              {connectedApacIntegration && (
                <Badge tone="promote" weight="regular">
                  {t('APACIntegration')}
                </Badge>
              )}
              {isNew && (
                <Badge tone="positive" weight="regular">
                  {t('new')}
                </Badge>
              )}
            </Inline>
          </Stack>
        </Box>
      </Box>
    </Card>
  );
};
